import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from 'components';
import { IGenericModal, Modal } from 'components/Modal/Modal';
import { H2 } from 'components/Typography/Typography';
import { Button } from 'components/_form';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Loader } from 'components/Loader/Loader';
import { ClosingTable } from 'components/ClosingTable/ClosingTable';
import { IAuditForm, IPostAuditFormRequest } from 'types/audits';
import {
  closeAuditFormClosing,
  openAuditFormClosing
} from 'api/auditFormsClosings';
import { IAuditFormsClosing } from 'types/auditFormsClosings';
import { useUserFunctionsQuery } from 'hooks/queries/useUserFunctionsQuery';
import { mapUserFunctionsToOptions } from 'utilities/userFunctions';
import { IUserSelectOption } from 'utilities/userSelectOption';
import {
  isClosingActionError,
  showRequestError
} from 'utilities/showRequestError';
import { toast } from 'react-toastify';

interface Props extends IGenericModal {
  watch: UseFormWatch<IPostAuditFormRequest['audit_form']>;
  setValue: UseFormSetValue<IPostAuditFormRequest['audit_form']>;
  usersList: IUserSelectOption[];
  selectedAuditForm?: IAuditForm;
  setSelectedAuditForm?: Dispatch<SetStateAction<IAuditForm | undefined>>;
}

export const AuditFormClosingModal = ({
  isOpen,
  onCancelClick,
  watch,
  setValue,
  usersList,
  selectedAuditForm,
  setSelectedAuditForm
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [isAddingRowVisible, setIsAddingRowVisible] = useState(false);
  const [closings, setClosings] = useState<IAuditFormsClosing[]>([]);

  const { t } = useTranslation();
  const { data: userFunctionsData = [] } = useUserFunctionsQuery();
  const userFunctionsList = useMemo(
    () => mapUserFunctionsToOptions(userFunctionsData),
    [userFunctionsData]
  );

  const closingIds = watch('closing_ids') || [];

  useEffect(() => {
    if (isOpen) {
      setClosings(selectedAuditForm?.audit_forms_closings || []);
    }
  }, [isOpen, selectedAuditForm?.id]);

  const applyClosingUpdate = (updatedClosing: IAuditFormsClosing) => {
    setClosings((prevClosings) => {
      const nextClosings = prevClosings.some(
        ({ id }) => id.toString() === updatedClosing.id.toString()
      )
        ? prevClosings.map((closing) =>
            closing.id.toString() === updatedClosing.id.toString()
              ? updatedClosing
              : closing
          )
        : [...prevClosings, updatedClosing];

      const allClosed =
        nextClosings.length > 0 &&
        nextClosings.every(({ closed_at }) => closed_at);

      setSelectedAuditForm?.((prevAuditForm) =>
        prevAuditForm
          ? {
              ...prevAuditForm,
              audit_forms_closings: nextClosings,
              closed: allClosed
            }
          : prevAuditForm
      );

      return nextClosings;
    });
  };

  const handleCloseAuditForm = async (closingId?: string | number) => {
    if (!closingId) return;

    setLoading(true);

    try {
      const closeAuditFormClosingRes = await closeAuditFormClosing(closingId);

      if (isClosingActionError(closeAuditFormClosingRes)) {
        showRequestError(closeAuditFormClosingRes.error, t);
        return;
      }

      if (closeAuditFormClosingRes.data) {
        applyClosingUpdate(closeAuditFormClosingRes.data);
        toast.success(t('common.success'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAuditForm = async (closingId?: string | number) => {
    if (!closingId) return;

    setLoading(true);

    try {
      const openAuditFormClosingRes = await openAuditFormClosing(closingId);

      if (isClosingActionError(openAuditFormClosingRes)) {
        showRequestError(openAuditFormClosingRes.error, t);
        return;
      }

      if (openAuditFormClosingRes.data) {
        applyClosingUpdate(openAuditFormClosingRes.data);
        toast.success(t('common.success'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      mainButton={{
        action: onCancelClick,
        variant: 'eucalyptus',
        label: loading ? <Loader /> : t(`buttons.saveAndClose`)
      }}
      gridTemplateColumns="50%"
      bodyMaxHeight="70vh"
      headerTitle={
        <Flex width="100%">
          <H2 variant="h3" mr={2}>
            {t('administrationAuditsView.closingForm')}
          </H2>
        </Flex>
      }
    >
      <Flex flexDirection="column">
        <Flex width="100%" justifyContent="flex-end">
          <Button
            variant="eucalyptus"
            onClick={() => {
              setIsAddingRowVisible(true);
            }}
          >
            <FontAwesomeIcon icon={faPlusCircle} /> {t('buttons.addPerson')}
          </Button>
        </Flex>
        <ClosingTable
          closingIds={closingIds}
          setClosingIds={(ids) => setValue('closing_ids', ids)}
          closings={closings}
          authorId={watch('author_id')}
          authorizedEditorIds={watch('person_authorized_to_edit_ids') || []}
          authorizedEditorRoleLabel={t('administrationAuditsView.authorizedToEdit')}
          usersList={usersList}
          userFunctionsList={userFunctionsList}
          participationsAttributes={[]}
          handleClose={handleCloseAuditForm}
          handleOpen={handleOpenAuditForm}
          isAddingRowVisible={isAddingRowVisible}
          setIsAddingRowVisible={setIsAddingRowVisible}
          allowRemoveAuthor
          allowAdminToggleClosing={false}
        />
      </Flex>
    </Modal>
  );
};
